// Sound notification utilities
class SoundManager {
  private audioContext: AudioContext | null = null;
  private isInitialized = false;

  // Initialize audio context on first user interaction
  async initialize() {
    if (this.isInitialized) return true;

    try {
      // Create audio context
      this.audioContext = new (window.AudioContext || (window as never)['webkitAudioContext'])();
      
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      this.isInitialized = true;
      console.log('🔊 Sound system initialized');
      return true;
    } catch (error) {
      console.warn('Sound initialization failed:', error);
      return false;
    }
  }

  // Play a notification beep
  async playNotificationBeep() {
    try {
      if (!this.isInitialized) {
        const initialized = await this.initialize();
        if (!initialized) {
          this.fallbackNotification();
          return;
        }
      }

      if (!this.audioContext) {
        this.fallbackNotification();
        return;
      }

      // Create a simple beep sound using Web Audio API
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Configure the beep sound
      oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime); // 800Hz frequency
      oscillator.type = 'sine';

      // Create an envelope for the sound
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

      // Play the sound
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.3);

      console.log('🔔 Notification beep played');
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
      this.fallbackNotification();
    }
  }

  // Play a multi-tone chime (longer and more noticeable)
  async playMultiToneChime() {
    try {
      if (!this.isInitialized) {
        const initialized = await this.initialize();
        if (!initialized) {
          this.fallbackNotification();
          return;
        }
      }

      if (!this.audioContext) {
        this.fallbackNotification();
        return;
      }

      // Create a multi-tone chime with 3 notes
      const startTime = this.audioContext.currentTime;
      const duration = 0.4; // Each note duration
      const gap = 0.1; // Gap between notes
      const totalDuration = (duration + gap) * 3 - gap;
      
      // Notes frequencies (C, E, G - major triad)
      const frequencies = [523.25, 659.25, 783.99];
      
      // Create a gain node for volume control
      const masterGain = this.audioContext.createGain();
      masterGain.gain.value = 0.3; // Set overall volume
      masterGain.connect(this.audioContext.destination);

      // Play each note in sequence
      frequencies.forEach((frequency, index) => {
        const noteTime = startTime + (duration + gap) * index;
        
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(masterGain);
        
        // Configure the note
        oscillator.frequency.setValueAtTime(frequency, noteTime);
        oscillator.type = 'sine';
        
        // Create an envelope for each note
        gainNode.gain.setValueAtTime(0, noteTime);
        gainNode.gain.linearRampToValueAtTime(1, noteTime + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, noteTime + duration);
        
        // Play the note
        oscillator.start(noteTime);
        oscillator.stop(noteTime + duration);
      });

      console.log('🎵 Multi-tone chime played');
      
      // Return a promise that resolves when the sound finishes
      return new Promise(resolve => setTimeout(resolve, totalDuration * 1000));
    } catch (error) {
      console.warn('Failed to play multi-tone chime:', error);
      this.fallbackNotification();
    }
  }

  // Fallback notification for when audio fails
  private fallbackNotification() {
    // Try HTML5 audio as fallback
    try {
      const audio = new Audio();
      // Simple beep encoded as data URL
      audio.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYfCBWKzfPEeDQGLYXO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYfCGS459cUXrbx+pdbDgxbpuTwuWQcCDGa2fLEeDQGLYXO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYfCGuQ2PK7fyoADGy8hOGsUgkKGGu0YeO5YwgONJjQ7tpHDgxCgdr52VgO';
      audio.volume = 0.3;
      audio.play().catch(() => {
        console.log('🔔 Audio blocked - using visual notification only');
      });
    } catch {
      console.log('🔔 Notification (visual only)');
    }
  }

  // Play order ready notification
  async playOrderReadyNotification() {
    if (!this.isInitialized) {
      await this.initialize();
    }
    await this.playNotificationBeep();
  }

  // Play new order notification (multi-tone chime)
  async playNewOrderNotification() {
    if (!this.isInitialized) {
      await this.initialize();
    }
    await this.playMultiToneChime();
  }

  // Test sound functionality
  async testSound() {
    console.log('🧪 Testing sound...');
    await this.playMultiToneChime();
  }
}

// Create global sound manager instance
export const soundManager = new SoundManager();

// Helper functions
export const playOrderReadySound = () => soundManager.playOrderReadyNotification();
export const playNewOrderSound = () => soundManager.playNewOrderNotification();
export const testNotificationSound = () => soundManager.testSound();
export const initializeSoundSystem = () => soundManager.initialize(); 